// src/wrap-with-provider.tsx
/*
This code is imported in to both gatsby-browser.js and gatsby-ssr.js to wrap the root element with the necessary providers.
 */
import React, {ReactNode, useMemo, useEffect, useRef} from "react";
import {ApolloProvider, ApolloClient, InMemoryCache, createHttpLink} from "@apollo/client";
import {useSelector} from "react-redux";
import {PersistGate} from "redux-persist/integration/react";
import {store, persistor, RootState} from "./store";
import {setContext} from "@apollo/client/link/context";
import {Provider} from "react-redux";
import {ThemeProvider} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./theme/theme";
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import {StyledEngineProvider} from "@mui/material";
import {CacheProvider} from "@emotion/react";
import createEmotionCache from "./utils/createEmotionCache";
import SessionProvider from "./components/SessionProvider/SessionProvider";
import {useClarityIdentify} from "./hooks/useClarityIdentify";
import MaintenancePage from "./components/MaintenancePage/MaintenancePage";

// Client-side Emotion cache (reused across renders)
const clientSideEmotionCache = createEmotionCache();

const isMaintenanceMode = process.env.GATSBY_MAINTENANCE_MODE === "true";

// Apollo Client creation
export const createApolloClient = (token: string | null) => {
    // Use GATSBY_ prefixed env var which is available in browser
    // Fallback to BACKEND_GRAPHQL_ENDPOINT for build-time (gatsby-source-wordpress)
    const graphqlEndpoint = process.env.GATSBY_BACKEND_URL 
        ? `${process.env.GATSBY_BACKEND_URL}/graphql`
        : process.env.BACKEND_GRAPHQL_ENDPOINT;
    
    const httpLink = createHttpLink({
        uri: graphqlEndpoint,
    });

    const authLink = setContext((_, {headers}) => {
        return {
            headers: {
                ...headers,
                // Use the token passed in argument
                authorization: token ? `Bearer ${token}` : "",  
            },
        };
    });

    return new ApolloClient({
        link: authLink.concat(httpLink),
        cache: new InMemoryCache({
            typePolicies: {
                // InvidooSettings is a singleton type (no ID) - tell Apollo to treat it as one
                InvidooSettings: {
                    keyFields: [], // Empty array = singleton (only one instance exists)
                    merge(existing = {}, incoming) {
                        // Deep merge to preserve fields from different queries
                        return { ...existing, ...incoming };
                    },
                },
                Query: {
                    fields: {
                        invidooSettings: {
                            // Merge different fields from InvidooSettings instead of replacing
                            merge(existing = {}, incoming) {
                                return { ...existing, ...incoming };
                            },
                        },
                    },
                },
            },
        }),
    });
};

interface WrapRootElementProps {
    element: ReactNode;
}

// Apollo Setup Component
const ApolloSetup: React.FC<WrapRootElementProps> = ({element}) => {
    const token = useSelector((state: RootState) => state.user.token); // Get token from Redux store
    const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);
    
    // Keep track of the last token to detect changes
    const lastTokenRef = useRef<string | null>(null);
    const clientRef = useRef<ApolloClient<any> | null>(null);

    // Identify returning authenticated users in Clarity (safe when Clarity not installed)
    useClarityIdentify();

    // Memoize the Apollo Client to prevent unnecessary re-creation
    const client = useMemo(() => {
        const tokenChanged = token !== lastTokenRef.current;
        const newClient = createApolloClient(token);
        
        // Token change creates new client with fresh cache
        lastTokenRef.current = token;
        clientRef.current = newClient;
        
        return newClient;
    }, [token, isAuthenticated]); // Include isAuthenticated to ensure proper state tracking

    return (
        <ApolloProvider client={client}>
            <SessionProvider>
                {element}
            </SessionProvider>
        </ApolloProvider>
    );
};

const isSSR = typeof window === 'undefined';

interface AppContentProps extends WrapRootElementProps {
    emotionCache?: ReturnType<typeof createEmotionCache>;
}

const AppContent: React.FC<AppContentProps> = ({element, emotionCache = clientSideEmotionCache}) => (
    <CacheProvider value={emotionCache}>
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <ApolloSetup element={element} />
                </LocalizationProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    </CacheProvider>
);

// Root wrapper that provides all contexts
const AppWrapper: React.FC<WrapRootElementProps> = ({element}) => {
    if (isMaintenanceMode) {
        return (
            <Provider store={store}>
                <CacheProvider value={clientSideEmotionCache}>
                    <StyledEngineProvider injectFirst>
                        <ThemeProvider theme={theme}>
                            <CssBaseline />
                            <MaintenancePage />
                        </ThemeProvider>
                    </StyledEngineProvider>
                </CacheProvider>
            </Provider>
        );
    }

    const content = <AppContent element={element} />;

    return (
        <Provider store={store}>
            {isSSR ? (
                content
            ) : (
                <PersistGate loading={content} persistor={persistor!}>
                    {content}
                </PersistGate>
            )}
        </Provider>
    );
};

// Export for Gatsby
export const wrapRootElement = AppWrapper;

export default AppWrapper;
